export const quickActionSchema = {
  $id: '/quickAction.schema.json',
  $schema: 'http://json-schema.org/draft-07/schema',
  title: 'QuickActionSchema',
  type: 'object',
  description: 'Json schema that represents the quick action content type.',
  additionalProperties: false,
  properties: {
    link: {
      type: 'object',
      description: 'A string url for external or internal links',
      properties: {
        to: {
          description: 'Url string',
          type: 'string',
        },
        replacements: {
          description:
            'Directive to interpolate a url with dynamic values taken from a resource.',
          type: 'array',
          items: {
            type: 'object',
            properties: {
              source: {
                type: 'object',
                description:
                  'An object that concludes a json path and a resource to switch json path contexts.',
                properties: {
                  jsonPath: {
                    description:
                      'The json path that points to a value of input data.',
                    type: 'string',
                  },
                  resource: {
                    type: 'string',
                    description:
                      'The data context of the query, it is used for 3rd party data like tenants, it defaults to _self which is the quick action data.',
                    enum: ['tenant', '_self', 'tokenClaims'],
                  },
                },
                required: ['jsonPath', 'resource'],
              },
              key: {
                description: 'The interpolation key.',
                type: 'string',
              },
            },
            required: ['key', 'source'],
          },
        },
        type: {
          type: 'string',
          enum: ['external-link', 'internal-link', 'internal-state'],
        },
      },
      required: ['type', 'to'],
    },
    baseUrl: {
      type: 'object',
      description:
        'The base url of the call to action url, based in environment.',
      properties: {
        dev: {
          type: 'string',
        },
        qa: {
          type: 'string',
        },
        'pre-production': {
          type: 'string',
        },
        preprod: {
          type: 'string',
        },
        production: {
          type: 'string',
        },
        staging: {
          type: 'string',
        },
      },
      required: ['dev', 'qa', 'pre-production', 'staging', 'production'],
    },
    color: {
      type: 'string',
      description:
        "A color string which is defined in the SDS and defines the icon's color and background color of the quick action. (only the colors that have 100 and 600 variations from SDS are supported",
      default: 'blackAlpha',
      enum: [
        'primary',
        'red',
        'gray',
        'blackAlpha',
        'whiteAlpha',
        'teal',
        'purple',
        'orange',
        'yellow',
        'green',
        'blue',
        'pink',
      ],
    },
    icon: {
      type: 'object',
      properties: {
        src: {
          description: 'The src url of an icon with type url.',
          type: 'string',
        },
        type: {
          type: 'string',
          description: 'The mdi code on an icon with type mdi.',
          enum: ['url', 'mdi', 'blok'],
        },
      },
      required: ['src', 'type'],
    },
    displayName: {
      type: 'object',
      description: 'The quick action description',
      properties: {
        name: {
          description: 'the base description',
          type: 'string',
        },
        replacements: {
          description:
            'Directive to interpolate a url with dynamic values taken from a resource.',
          type: 'array',
          items: {
            type: 'object',
            properties: {
              source: {
                type: 'object',
                description:
                  'An object that concludes a json path and a resource to switch json path contexts.',
                properties: {
                  jsonPath: {
                    description:
                      'The json path that points to a value of input data.',
                    type: 'string',
                  },
                  resource: {
                    type: 'string',
                    description:
                      'The data context of the query, it is used for 3rd party data like tenants, it defaults to _self which is the product data.',
                    enum: ['tenant', '_self', 'tokenClaims'],
                  },
                  regex: {
                    type: 'object',
                    properties: {
                      match: {
                        description: 'Regex pattern for matching',
                        type: 'string',
                      },
                      replaceValue: {
                        description:
                          'The replacement value for the regex match.',
                        type: 'string',
                      },
                    },
                    required: ['match', 'replaceValue'],
                  },
                },
                required: ['jsonPath', 'resource'],
              },
              key: {
                description: 'The interpolation key.',
                type: 'string',
              },
            },
            required: ['key', 'source'],
          },
        },
      },
    },
    type: {
      type: 'string',
      description:
        'The quick action type, ui type is used mainly for portal links that are not applications, app stands for applications like tenant quick actions.',
      enum: ['app', 'ui'],
    },
    name: {
      description: 'The quick action title',
      type: 'string',
    },
    envAccess: {
      type: 'array',
      description: 'Defines for which environment is enabled',
      items: {
        type: 'string',
        enum: ['dev', 'qa', 'staging', 'pre-production', 'production'],
      },
    },
    appAccess: {
      type: 'array',
      description: 'Defines for which app role is enabled',
      items: {
        type: 'string',
        enum: [
          'User',
          'Admin',
          'Developer',
          'Tech Admin',
          'Viewer',
          'Read only',
          'Analytics user',
        ],
      },
    },
    orgAccess: {
      type: 'array',
      description: 'Defines for which org access is enabled',
      items: {
        type: 'string',
        enum: ['Organization Admin', 'Organization Owner', 'Organization User'],
      },
    },
    productAccess: {
      type: 'array',
      description:
        'It is used for filtering, it defines which quick actions are enabled by product code(s)',
      items: {
        type: 'string',
        enum: [
          'NO_PRODUCTS',
          'HeadlessCMS',
          'XMCloud',
          'OrderCloud',
          'Connect',
          'CDP',
          'Personalize',
          'Discover',
          'Search',
          'Send',
          'DAM',
          'ContentOperations',
          'ai',
        ],
      },
    },
    openInTab: {
      type: 'boolean',
      default: false,
    },
    location: {
      type: 'array',
      description: 'The panel(s) where is meant to be shown',
      items: {
        type: 'string',
        default: 'quick-actions',
        enum: ['quick-actions'],
      },
    },
    'analytics-id': {
      type: 'string',
      description: 'The id for data-behavior-analytics-id',
    },
    category: {
      type: 'string',
      description: 'The category of the quick action',
    },
  },
  required: ['link', 'type', 'name', 'icon', 'color'],
};
